import { useQuery } from '@apollo/client';
import { Paper, ThemeProvider, createTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { AppFooter, Spinner } from 'components';
import { useQuizContext } from 'contexts';
import { getQuizQuestions } from 'graphql/queries';
import {
    QuizQuestions,
    QuizQuestionsVariables
    // QuizQuestions_quiz_master_quizzes_by_pk_questions
} from 'graphql/queries/types/QuizQuestions';
import { useQuizFocus } from 'hooks';
import { useEffect } from 'react';
import { indexToLetter } from 'utils';

interface QuestionAnswer {
    questionId: number;
    questionIndex: number;
    answerType: 'string' | 'radiobutton' | 'list' | 'checkboxes'; // TODO - list and checkbox types not implemeted as possible question answers yet.
    answerOptions: number;
    correctAnswers: number;
}

interface Props {}

export const PrintAnswerSheet = (props: Props) => {
    const quiz = useQuizContext();
    useQuizFocus();

    const printTheme = createTheme({ palette: { mode: 'light' } });

    const {
        loading,
        // error,
        data: quizQuestions
        // refetch
    } = useQuery<QuizQuestions, QuizQuestionsVariables>(getQuizQuestions, {
        variables: { quizId: quiz.quiz.id! }
    });

    useEffect(() => {
        if (quizQuestions?.quiz_master_quizzes_by_pk?.questions && quizQuestions.quiz_master_quizzes_by_pk?.questions.length > 0) {
            window.print();
        }
    }, [quizQuestions]);

    const generateQuestionAnswerForm = (answer: QuestionAnswer): React.ReactNode => {
        if (answer.answerType === 'radiobutton') {
            const optionNodes = [];
            for (let rbIndex = 0; rbIndex < answer.answerOptions; rbIndex++) {
                optionNodes.push(<Typography key={`option-${rbIndex}`}>{`${indexToLetter(rbIndex)})`}</Typography>);
            }
            return (
                <Stack direction="row" spacing={2}>
                    <Typography marginRight={5}>{`${answer.questionIndex + 1}.`}</Typography>
                    <Stack direction={'row'} sx={{ justifyContent: 'space-evenly', flexGrow: 0.6 }}>
                        {optionNodes}
                    </Stack>
                </Stack>
            );
        }

        // answerType === 'string'
        return (
            <Stack direction={'row'} spacing={2}>
                <Typography>{`${answer.questionIndex + 1}.`}</Typography>
                <Box sx={{ borderBottom: 'solid 1px #000', width: '100%', display: 'inline-block' }} />
            </Stack>
        );
    };

    const renderPrintGrid = (quizQuestions: QuizQuestions) => {
        // answers per page - 26
        const maxAnswerRowsFirstPage = 26;
        const maxAnswerRowsPerPage = 30;
        const pages: QuestionAnswer[][] = [];
        let pageAnswers: QuestionAnswer[] = [];

        let currentPageAnswerRows = 0;
        quizQuestions?.quiz_master_quizzes_by_pk?.questions.forEach((q, index) => {
            const qAnswers = q.answers.filter((a) => a.isCorrect);
            const answerType = q.answers.length > 1 ? 'radiobutton' : 'string';

            currentPageAnswerRows += qAnswers.length;
            if ((pages.length === 0 && currentPageAnswerRows > maxAnswerRowsFirstPage)
            || currentPageAnswerRows > maxAnswerRowsPerPage) {
                pages.push([...pageAnswers]);
                console.log('Page ', pages.length + 1, pageAnswers);
                pageAnswers = [];
                currentPageAnswerRows = qAnswers.length;
            }

            pageAnswers.push({
                questionId: q.id,
                questionIndex: index,
                answerType,
                correctAnswers: qAnswers.length,
                answerOptions: q.answers.length
            });
        });
        pages.push([...pageAnswers]);

        const pageGrids: React.ReactNode[] = [];
        pages.forEach((pageAnswers, index) => {
            pageGrids.push(
                <Grid container direction="row" key={`page-${index}`}>
                    <Grid item sx={{ padding: '10px 20px' }} xs={6}>
                        <Grid container spacing={4} direction="column">
                            {pageAnswers.slice(0, Math.floor(pageAnswers.length / 2)).map((a) => (
                                <Grid item key={a.questionId}>
                                    {generateQuestionAnswerForm(a)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                    <Grid item sx={{ padding: '10px 20px' }} xs={6}>
                        <Grid container spacing={4} direction="column">
                            {pageAnswers.slice(Math.floor(pageAnswers.length / 2), pageAnswers.length).map((a) => (
                                <Grid item key={a.questionId}>
                                    {generateQuestionAnswerForm(a)}
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            );

            if (index < pages.length - 1) {
                pageGrids.push(<div key={`page-${index}`} style={{ breakAfter: 'page' }} />);
            }
        });

        return pageGrids;
    };

    return loading ? (
        <Spinner />
    ) : (
        <ThemeProvider theme={printTheme}>
            <Paper sx={{ height: '100%', border: 'none', boxShadow: 'none', transition: 'none' }}>
                <Typography variant="h5" paddingBottom={5} textAlign="center">
                    {quiz.quiz.name}
                </Typography>
                {renderPrintGrid(quizQuestions!)}
            </Paper>
            <AppFooter />
        </ThemeProvider>
    );
};
